/**
 * Support widget — the working-hours sentence.
 *
 * The endpoint publishes the schedule as values rather than prose: the response is
 * served from a shared cache that cannot vary by language, so composing the sentence
 * is the frontend's job. Both surfaces did it themselves and the two had already
 * drifted — different time separator, and only one of them sorted the weekdays or
 * checked they were contiguous before rendering "Monday to Friday".
 *
 * The copy stays with the caller, because the two read different catalogues: Django
 * fills a JSON script tag with `{% translate %}`, fe-react calls `t()`. What they hand
 * over is the *raw* template, un-interpolated, so the same msgid and the same
 * placeholder names work on either side and one translation covers both.
 *
 * No date arithmetic and no availability decision here — whether support is open is
 * `state`, resolved server-side, and nothing in this file may second-guess it.
 */
import type { SupportApiSchedule } from "./api.types"

const ISO_MONDAY = 1
const ISO_SUNDAY = 7

const DEFAULT_LIST_SEPARATOR = ", "

/**
 * The schedule, in the shape the apps hold it (`normalize.ts`). Taken instead of the
 * wire type so the camelCase side does not have to convert back into snake_case just to
 * render a sentence; `scheduleFromApi` covers the surfaces that still hold the payload.
 */
export interface SupportHoursSchedule {
  weekdays?: number[]
  opensAt: string
  closesAt: string
}

/** The wire schedule as this module wants it. */
export function scheduleFromApi(schedule: SupportApiSchedule | undefined): SupportHoursSchedule | undefined {
  if (!schedule) {
    return undefined
  }

  return { weekdays: schedule.weekdays, opensAt: schedule.opens_at, closesAt: schedule.closes_at }
}

/** The translated fragments this module needs, in whatever way the surface got them. */
export interface SupportHoursCopy {
  /** `T.SUPPORT-WIDGET.HOURS` — placeholders: `days`, `start`, `end`. */
  hoursTemplate: string
  /** `T.SUPPORT-WIDGET.DAY-RANGE` — placeholders: `first`, `last`. */
  dayRange: string
  /** `T.SUPPORT-WIDGET.WEEKDAY-1` … `-7`, keyed by ISO weekday as a string. */
  weekdays: { [isoWeekday: string]: string }
  /** Separator between the names of a non-contiguous run. Defaults to `", "`. */
  listSeparator?: string
}

/**
 * Fill `%(name)s` (Django/gettext) or `{{name}}` (i18next) placeholders.
 *
 * Both syntaxes are accepted so a single msgid can be served from either catalogue
 * without the translation having to know which surface will render it. An unknown
 * name resolves to an empty string rather than being left as a visible placeholder.
 */
export function interpolate(template: string, values: { [name: string]: string }): string {
  return template.replace(/%\((\w+)\)s|\{\{\s*(\w+)\s*\}\}/g, (_match, gettextName, mustacheName) => {
    const name = gettextName || mustacheName

    return name in values ? values[name] : ""
  })
}

/** `'08:00'` → `'08.00'` — the separator the designs use. */
export function formatSupportTime(time: string): string {
  return time.replace(":", ".")
}

function isContiguous(weekdays: number[]): boolean {
  return weekdays.every((day, index) => index === 0 || day === weekdays[index - 1] + 1)
}

/**
 * `'Monday to Friday'` for a contiguous run, otherwise the days listed out. Empty when
 * there is nothing nameable — the caller drops the whole line on `""`.
 *
 * Days the backend sent out of order, out of range, or with no translated name are
 * dropped first, so the range endpoints are always two days we can actually name.
 */
export function formatSupportDays(weekdays: number[] | undefined, copy: SupportHoursCopy): string {
  const days = (weekdays || [])
    .filter(day => day >= ISO_MONDAY && day <= ISO_SUNDAY && Boolean(copy.weekdays[String(day)]))
    .sort((first, second) => first - second)
  const names = days.map(day => copy.weekdays[String(day)])

  if (names.length === 0) {
    return ""
  }
  if (names.length === 1) {
    return names[0]
  }
  // A gap means the run is not a range: "Monday to Sunday" would promise a Saturday
  // the backend never sent, so list the days instead.
  if (!isContiguous(days)) {
    return names.join(copy.listSeparator || DEFAULT_LIST_SEPARATOR)
  }

  return interpolate(copy.dayRange, { first: names[0], last: names[names.length - 1] })
}

/**
 * The full sentence. Empty when the schedule cannot produce one — half a sentence is
 * worse than no line at all, and every surface drops the line on `""`.
 */
export function formatSupportHours(
  schedule: SupportHoursSchedule | undefined,
  copy: SupportHoursCopy | undefined | null
): string {
  if (!schedule || !copy) {
    return ""
  }
  const days = formatSupportDays(schedule.weekdays, copy)
  if (!days || !schedule.opensAt || !schedule.closesAt) {
    return ""
  }

  return interpolate(copy.hoursTemplate, {
    days,
    start: formatSupportTime(schedule.opensAt),
    end: formatSupportTime(schedule.closesAt)
  })
}
